import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEventListener, useUnmountEffect } from 'primereact/hooks';
import { useContext, useEffect, useRef } from 'react';
import { classNames } from 'primereact/utils';
import AppFooter from './AppFooter';
import AppSidebar from './AppSidebar';
import AppTopbar from './AppTopbar';
import AppCallToolbar from './AppCallToolbar';
import CrmChatWidget from '@/pages/crm-chat/CrmChatWidget';
import AppConfig from './AppConfig';
import { LayoutContext } from './context/layoutcontext';
import { useLocation } from 'react-router-dom';
import { ConfirmPopup } from 'primereact/confirmpopup';
import { AgentInboundCallBridge } from '@/layout/AgentInboundCallBridge';
import { SipCallProvider } from '@/shared/providers/SipCallProvider';
import { CrmChatProvider } from '@/shared/providers/CrmChatProvider';
import { BreakEventsProvider } from '@/shared/providers/BreakEventsProvider';
import { SessionEventsProvider } from '@/shared/providers/SessionEventsProvider';
import { STOMP_WS_ENABLED } from '@/config/stomp-ws';
import { CRM_CHAT_ENABLED } from '@/pages/crm-chat/crm-chat-setup';
import { useAuthenticatedRefresh } from '@/shared/auth/useAuthenticatedRefresh';
import { useCrmProfile } from '@/shared/hooks/useCrmProfile';
const Layout = ({ children }) => {
    useAuthenticatedRefresh();
    useCrmProfile();
    const layoutBody = (_jsx(CrmChatProvider, { children: _jsx(LayoutChrome, { children: children }) }));
    return (_jsxs(SipCallProvider, { children: [_jsx(AgentInboundCallBridge, {}), STOMP_WS_ENABLED ? (_jsx(BreakEventsProvider, { children: _jsx(SessionEventsProvider, { children: layoutBody }) })) : (layoutBody)] }));
};
function LayoutChrome({ children }) {
    const { layoutConfig, layoutState, setLayoutState } = useContext(LayoutContext);
    const topbarRef = useRef(null);
    const sidebarRef = useRef(null);
    const [bindMenuOutsideClickListener, unbindMenuOutsideClickListener] = useEventListener({
        type: 'click',
        listener: (event) => {
            const isOutsideClicked = !(sidebarRef.current?.isSameNode(event.target) ||
                sidebarRef.current?.contains(event.target) ||
                topbarRef.current?.menubutton?.isSameNode(event.target) ||
                topbarRef.current?.menubutton?.contains(event.target));
            if (isOutsideClicked) {
                hideMenu();
            }
        }
    });
    const location = useLocation();
    useEffect(() => {
        hideMenu();
        hideProfileMenu();
    }, [location.pathname, location.search]);
    const [bindProfileMenuOutsideClickListener, unbindProfileMenuOutsideClickListener] = useEventListener({
        type: 'click',
        listener: (event) => {
            const isOutsideClicked = !(topbarRef.current?.topbarmenu?.isSameNode(event.target) ||
                topbarRef.current?.topbarmenu?.contains(event.target) ||
                topbarRef.current?.topbarmenubutton?.isSameNode(event.target) ||
                topbarRef.current?.topbarmenubutton?.contains(event.target));
            if (isOutsideClicked) {
                hideProfileMenu();
            }
        }
    });
    const hideMenu = () => {
        setLayoutState((prevLayoutState) => ({
            ...prevLayoutState,
            overlayMenuActive: false,
            staticMenuMobileActive: false,
            menuHoverActive: false
        }));
        unbindMenuOutsideClickListener();
        unblockBodyScroll();
    };
    const hideProfileMenu = () => {
        setLayoutState((prevLayoutState) => ({
            ...prevLayoutState,
            profileSidebarVisible: false
        }));
        unbindProfileMenuOutsideClickListener();
    };
    const blockBodyScroll = () => {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        }
        else {
            document.body.className += ' blocked-scroll';
        }
    };
    const unblockBodyScroll = () => {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        }
        else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    };
    useEffect(() => {
        if (layoutState.overlayMenuActive || layoutState.staticMenuMobileActive) {
            bindMenuOutsideClickListener();
        }
        layoutState.staticMenuMobileActive && blockBodyScroll();
    }, [layoutState.overlayMenuActive, layoutState.staticMenuMobileActive]);
    useEffect(() => {
        if (layoutState.profileSidebarVisible) {
            bindProfileMenuOutsideClickListener();
        }
    }, [layoutState.profileSidebarVisible]);
    useUnmountEffect(() => {
        unbindMenuOutsideClickListener();
        unbindProfileMenuOutsideClickListener();
    });
    const containerClass = classNames('layout-wrapper', {
        'layout-overlay': layoutConfig.menuMode === 'overlay',
        'layout-static': layoutConfig.menuMode === 'static',
        'layout-static-inactive': layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
        'layout-overlay-active': layoutState.overlayMenuActive,
        'layout-mobile-active': layoutState.staticMenuMobileActive,
        'p-input-filled': layoutConfig.inputStyle === 'filled',
        'p-ripple-disabled': !layoutConfig.ripple
    });
    return (_jsxs(_Fragment, { children: [_jsx(ConfirmPopup, {}), _jsxs("div", { className: containerClass, children: [_jsx(AppTopbar, { ref: topbarRef }), _jsx(AppCallToolbar, {}), _jsx("div", { ref: sidebarRef, className: "layout-sidebar", children: _jsx(AppSidebar, {}) }), _jsxs("div", { className: "layout-main-container", children: [_jsx("div", { className: "layout-main", children: children }), _jsx(AppFooter, {})] }), _jsx(AppConfig, {}), CRM_CHAT_ENABLED ? _jsx(CrmChatWidget, {}) : null, _jsx("div", { className: "layout-mask" })] })] }));
}
export default Layout;
